import {
  ArrowNarrowRightIcon,
  RefreshIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from '@heroicons/react/outline';
import { Chart, ChartOptions, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { TimeseriesMetricSelector } from 'components/analytics/ExecutiveSummary/TimeseriesMetricSelector';
import { useExecutiveSummaryState } from 'components/analytics/ExecutiveSummary/useExecutiveSummaryState';
import { GroupBy } from 'components/dashboard/StoreMetrics/types';
import { ExportIcon } from 'components/icons';
import { CardBackground, Skeleton, Tooltip } from 'components/ui';
import { format, parse, subYears } from 'date-fns';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import { TimeSeries } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { filters } from './categoryFilters';
import { formatNumber } from './helpers';
import { TimeseriesGroupByMenu } from './TimeseriesGroupByMenu';

const ExportMenuLazy = lazy(
  () =>
    import('components/analytics/ExecutiveSummary/ExecutiveSummaryExportMenu')
);

export const TimeseriesChart = () => {
  const {
    selectedTimeseriesMetric,
    previousStartDate,
    previousEndDate,
    selectedCategory,
    endDate,
    startDate,
    metricList,
    groupBy,
    groupByOptions,
  } = useExecutiveSummaryState();

  const { currentStore } = useAppSelector(userDetailsState);
  const { output } = useAppSelector(composableFiltersState);

  const selectedCategoryFilters = useMemo(() => {
    if (selectedCategory === 'overall') {
      return output.filtersApiPayload;
    }

    return filters[selectedCategory]({ startDate, endDate });
  }, [endDate, output.filtersApiPayload, selectedCategory, startDate]);

  const previousCategoryFilters = useMemo(() => {
    if (selectedCategory === 'overall') {
      return output.filtersApiPayload;
    }

    return filters[selectedCategory]({
      startDate: previousStartDate,
      endDate: previousEndDate,
    });
  }, [
    previousEndDate,
    previousStartDate,
    output.filtersApiPayload,
    selectedCategory,
  ]);

  const { data: selectedMetrics, isFetching: isSelectedMetricsLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: selectedCategoryFilters,
        params: {
          metric_list: metricList,
          end_date: endDate,
          start_date: startDate,
          store_name: String(currentStore.completeInfo?.store_name),
        },
      },
      { skip: !currentStore.completeInfo?.store_name }
    );

  const { data: previousMetrics, isFetching: isPreviousMetricsLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: previousCategoryFilters,
        params: {
          metric_list: metricList,
          end_date: previousEndDate,
          start_date: previousStartDate,
          store_name: String(currentStore.completeInfo?.store_name),
        },
      },
      { skip: !currentStore.completeInfo?.store_name }
    );

  const currency = currentStore?.completeInfo?.currency || 'USD';
  const locale = currentStore?.completeInfo?.primary_locale || 'en-US';

  const selectedMetricValue = useMemo(() => {
    const metric =
      selectedMetrics &&
      (selectedMetrics[selectedTimeseriesMetric.id] as TimeSeries[]);
    if (!metric || metric?.length === 0) return null;

    return metric[0].value;
  }, [selectedMetrics, selectedTimeseriesMetric]);

  const previousMetricValue = useMemo(() => {
    const metric =
      previousMetrics &&
      (previousMetrics[selectedTimeseriesMetric.id] as TimeSeries[]);
    if (!metric || metric?.length === 0) return null;

    return metric[0].value;
  }, [previousMetrics, selectedTimeseriesMetric]);

  const change = useMemo(() => {
    if (isPreviousMetricsLoading || isSelectedMetricsLoading) return null;

    if (!selectedMetricValue || !previousMetricValue) return 0;

    return (selectedMetricValue - previousMetricValue) / previousMetricValue;
  }, [
    selectedMetricValue,
    previousMetricValue,
    isPreviousMetricsLoading,
    isSelectedMetricsLoading,
  ]);

  const TrendIcon = ({ value }: { value: number | null }) => {
    if (value === null) return <ArrowNarrowRightIcon className="w-5 h-5" />;

    if (value > 0) return <TrendingUpIcon className="w-5 h-5 text-green-500" />;

    if (value < 0) return <TrendingDownIcon className="w-5 h-5 text-red-500" />;

    return <ArrowNarrowRightIcon className="w-5 h-5" />;
  };

  const chartContainerRef = useRef<HTMLDivElement>(null);

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-executive_summary_timeseries-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  // Timeseries Date

  const { data: selectedRawData, isFetching: isSelectedDataLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: selectedCategoryFilters,
        params: {
          metric_list: [selectedTimeseriesMetric.id],
          end_date: endDate,
          start_date: startDate,
          store_name: String(currentStore.completeInfo?.store_name),
          ticking: groupBy,
        },
      },
      {
        skip:
          !currentStore.completeInfo?.store_name ||
          !groupByOptions.includes(groupBy as GroupBy),
      }
    );

  const { data: previousRawData, isFetching: isPreviousDataLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: previousCategoryFilters,
        params: {
          metric_list: [selectedTimeseriesMetric.id],
          end_date: previousEndDate,
          start_date: previousStartDate,
          store_name: String(currentStore.completeInfo?.store_name),
          ticking: groupBy,
        },
      },
      {
        skip:
          !currentStore.completeInfo?.store_name ||
          !groupByOptions.includes(groupBy as GroupBy),
      }
    );

  const selectedPeriodData = useMemo(() => {
    if (!selectedRawData) return [];

    return selectedRawData[selectedTimeseriesMetric.id] as TimeSeries[];
  }, [selectedRawData, selectedTimeseriesMetric.id]);

  const previousPeriodData = useMemo(() => {
    if (!previousRawData) return [];

    return previousRawData[selectedTimeseriesMetric.id] as TimeSeries[];
  }, [previousRawData, selectedTimeseriesMetric.id]);

  const isChartDataLoading = isSelectedDataLoading || isPreviousDataLoading;

  const chartData = useMemo(() => {
    return {
      labels: selectedPeriodData?.map((item) => item.date) || [],
      datasets: [
        {
          label: 'Selected',
          data: selectedPeriodData?.map((item) => item.value) || [],
          fill: false,
          borderColor: '#125970',
          tension: 0.1,
          pointRadius: 8,
          pointBackgroundColor: '#125970',
          pointBorderColor: 'white',
          pointBorderWidth: 3,
        },
        {
          label: 'Previous',
          data: previousPeriodData?.map((item) => item.value) || [],
          fill: false,
          borderColor: '#E1E1E1',
          tension: 0.1,
          pointRadius: 0,
        },
      ],
    };
  }, [selectedPeriodData, previousPeriodData]);

  const [isZoomed, setIsZoomed] = useState(false);

  // Reset zoom if params are changed
  useEffect(() => {
    setIsZoomed(false);
  }, [groupBy, selectedCategory, selectedTimeseriesMetric.id]);

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Category', key: 'category' },
      { label: 'Metric', key: 'metric' },
      { label: 'Group by', key: 'groupBy' },

      { label: 'Date', key: 'date' },
      { label: 'Value', key: 'value' },
    ];

    const previousDataValues =
      previousPeriodData?.map((item) => ({
        category: selectedCategory,
        metric: selectedTimeseriesMetric.label,
        groupBy,
        date: item.date || '',
        value:
          formatNumber(item.value, {
            type: selectedTimeseriesMetric.format,
            locale,
            currency,
          }) || '',
      })) || [];

    const selectedDataValues =
      selectedPeriodData?.map((item) => ({
        category: selectedCategory,
        metric: selectedTimeseriesMetric.label,
        groupBy,
        date: item.date || '',
        value:
          formatNumber(item.value, {
            type: selectedTimeseriesMetric.format,
            locale,
            currency,
          }) || '',
      })) || [];

    const csvData = [...previousDataValues, ...selectedDataValues];

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [
    currency,
    groupBy,
    locale,
    previousPeriodData,
    selectedCategory,
    selectedPeriodData,
    selectedTimeseriesMetric.format,
    selectedTimeseriesMetric.label,
  ]);

  const options: ChartOptions = {
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      zoom: {
        zoom: {
          wheel: {
            enabled: false,
          },
          pinch: {
            enabled: false,
          },
          drag: {
            enabled: true,
          },
          mode: 'x',
          onZoomComplete: ({ chart }) => {
            if (chart.isZoomedOrPanned()) {
              setIsZoomed(true);
            } else {
              setIsZoomed(false);
            }
          },
        },
      },
      legend: {
        labels: {
          pointStyle: 'circle',
          usePointStyle: true,
          font: {
            size: 13,
          },
        },
      },
      tooltip: {
        usePointStyle: true,
        padding: 16,
        backgroundColor: '#0B3543',
        cornerRadius: 6,
        bodySpacing: 8,
        titleMarginBottom: 10,
        boxPadding: 5,
        callbacks: {
          title: function () {
            return 'Comparison Year over Year';
          },
          label: (context) => {
            let label;
            if (context.dataset.label === 'Previous') {
              const parsedDate = format(
                subYears(parse(context.label, 'yyyy-MM-dd', new Date()), 1),
                'yyyy-MM-dd'
              );

              label = context.dataset.label + ' (' + parsedDate + ') ';
            } else {
              label = context.dataset.label + ' (' + context.label + ') ';
            }

            label += ': ';

            if (context.parsed.y !== null) {
              label += formatNumber(context.parsed.y, {
                type: selectedTimeseriesMetric.format,
                locale,
                currency,
              });
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (tickValue) =>
            formatNumber(tickValue, {
              type: selectedTimeseriesMetric.format,
              currency,
              locale,
            }),
        },
      },
      x: {
        ticks: {
          callback: function (value) {
            const parsedDate = parse(
              this.getLabelForValue(value as number),
              'yyyy-MM-dd',
              new Date()
            );

            if (groupBy === 'week') {
              return `${format(parsedDate, 'MMM-dd')}`;
            }

            if (groupBy === 'month') {
              return `${format(parsedDate, 'MMM')}`;
            }

            return `${format(parsedDate, 'MMM')} - ${format(parsedDate, 'dd')}`;
          },
        },
      },
    },
  };

  Chart.register(...registerables);

  const chartRef = useRef<any>(null);
  let chartInstance = useRef<any>(null);

  useEffect(() => {
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: options,
      plugins: [zoomPlugin],
    });
    return () => chartInstance.current.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  return (
    <div className="mt-10">
      <div className="flex justify-between">
        <div>
          <p className="text-2xl tracking-wide capitalize">
            {selectedCategory}
          </p>
          <div className="flex justify-between">
            <p className="mt-1 text-base text-left text-black ">
              Summary metrics for{' '}
              <span className="font-bold text-segments-link">
                {startDate ?? '...'} - {endDate ?? '...'}
              </span>
            </p>
          </div>
        </div>
        <div>
          <Suspense
            fallback={
              <div className="flex items-center p-3 rounded-full bg-segments-gray focus:outline-none">
                <ExportIcon className="w-5 h-5" />
              </div>
            }
          >
            <ExportMenuLazy
              containerRef={chartContainerRef}
              filename={filename}
              generateCSVData={generateCSVData}
            >
              <div className="p-3 rounded-full bg-segments-gray focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                <ExportIcon className="w-5 h-5" />
              </div>
            </ExportMenuLazy>
          </Suspense>
        </div>
      </div>
      <div className="mt-5" ref={chartContainerRef}>
        <CardBackground>
          <div className="p-10">
            <div className="flex items-center justify-between">
              <TimeseriesMetricSelector />
              <p className="text-left text-black text-segments-13px text-opacity-30">
                Comparison Year over Year
              </p>
            </div>
            <div className="mt-4">
              <Skeleton
                isLoaded={!isSelectedMetricsLoading}
                className="inline-block"
              >
                <p className="leading-9 text-segments-32px">
                  {formatNumber(selectedMetricValue, {
                    type: selectedTimeseriesMetric.format,
                    currency,
                    locale,
                  }) || '--'}
                </p>
              </Skeleton>

              <div className="mt-2">
                <Skeleton
                  isLoaded={
                    !isPreviousMetricsLoading || !isSelectedMetricsLoading
                  }
                  className="inline-block"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className={`flex items-center space-x-1 ${
                        (change || 0) > 0 ? 'text-green-500' : 'text-red-500'
                      }`}
                    >
                      <TrendIcon value={change} />
                      <p>
                        {formatNumber(change, {
                          type: 'percent',
                          locale,
                          currency,
                        })}
                      </p>
                    </div>
                    <p className="text-black text-opacity-30">
                      compared to{' '}
                      <span>
                        {formatNumber(previousMetricValue, {
                          type: selectedTimeseriesMetric.format,
                          locale,
                          currency,
                        })}
                      </span>{' '}
                      during {previousStartDate} - {previousEndDate}
                    </p>
                  </div>
                </Skeleton>
              </div>
            </div>
            <div className="mt-4 ">
              <div className="inline-block bg-segments-accent-color-orange bg-opacity-10 py-[10px] px-[18px] rounded-lg border-l-segments-accent-color-orange border-l-[5px]">
                <p>{selectedTimeseriesMetric.description}</p>
              </div>
            </div>
            <div className="h-full mt-4">
              <Skeleton isLoaded={!isChartDataLoading} className="h-[430px]">
                <div className="flex items-center justify-end -mb-9">
                  <p className="ml-12 italic text-gray-500 text-segments-13px">
                    Drag to zoom
                  </p>
                  <TimeseriesGroupByMenu />
                </div>

                <div className="chartjs-wrapper">
                  {isZoomed && (
                    <div className="relative">
                      <button
                        className="absolute px-2 py-2 text-white rounded-full right-6 top-12 bg-segments-accent-color-1 "
                        onClick={() => {
                          chartInstance.current.resetZoom();
                          setIsZoomed(false);
                        }}
                      >
                        <Tooltip text="Reset zoom">
                          <RefreshIcon className="w-5 h-5" />
                        </Tooltip>
                      </button>
                    </div>
                  )}
                  <canvas ref={chartRef} className="chartjs"></canvas>
                </div>
              </Skeleton>
            </div>
          </div>
        </CardBackground>
      </div>
    </div>
  );
};
